import { CreateNewFolder, FolderSpecial, TrendingUp, People, CheckCircle, PersonAdd, Archive, PlayArrow, OpenInNew } from "@mui/icons-material";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Chip, Avatar, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminProjectContext } from "../../../contexts/AdminProjectContext";
import { Project } from "../../../Models/Project";
import { ADMIN_VIEWS, PROJECT_STATUS, SELECTED_PROJECT_STORAGE_KEY } from "../../../utils/constant";
import AdminProjectEditingModal from '../modal/AdminProjectEditingModal';
import AdminAddProjectParticipantsModal from '../modal/AdminAddProjectParticipantsModal';
import styled from "styled-components";
import { formatAmount } from "../../../utils/utilities";

// Styled Components pour le design moderne
const ProjectsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: fadeInUp 0.8s ease-out;
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);

  @media (max-width: 768px) {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SectionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiSvgIcon-root {
    color: var(--text-primary);
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;

    .MuiSvgIcon-root {
      font-size: 1rem;
    }
  }
`;

const AddProjectButton = styled.button`
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    border-radius: 8px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const StatCard = styled(Card)`
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px);
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: visible !important;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
    background: rgba(255, 255, 255, 0.95) !important;
  }

  @media (max-width: 768px) {
    border-radius: 12px !important;

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const StatCardContent = styled(CardContent)`
  padding: 2rem !important;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem !important;
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
`;

const StatIconContainer = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;

  .MuiSvgIcon-root {
    color: var(--text-primary);
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    border-radius: 10px;

    .MuiSvgIcon-root {
      font-size: 1rem;
    }
  }
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0;
  }
`;

const StatLabel = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.6rem;
    line-height: 1.2;
  }
`;

const ProjectsTableCard = styled(Card)`
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px);
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
`;

const TableHeader = styled(TableHead)`
  background: rgba(102, 126, 234, 0.1);
  
  .MuiTableCell-head {
    font-weight: 600;
    color: var(--text-primary);
    border-bottom: 2px solid rgba(102, 126, 234, 0.2);
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(102, 126, 234, 0.05) !important;
    transform: translateX(4px);
  }
  
  .MuiTableCell-root {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding: 1rem;
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ProjectAvatar = styled(Avatar)`
  width: 40px !important;
  height: 40px !important;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color)) !important;
  font-weight: 600 !important;
`;

const ProjectName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
`;

const StatusChip = styled(Chip)<{ status?: string }>`
  &.MuiChip-root {
    background: ${props => {
      switch (props.status?.toLowerCase()) {
        case 'actif': return 'rgba(67, 233, 123, 0.1)';
        case 'terminé': return 'rgba(102, 126, 234, 0.1)';
        case 'en pause': return 'rgba(255, 193, 7, 0.1)';
        default: return 'rgba(102, 126, 234, 0.1)';
      }
    }};
    color: ${props => {
      switch (props.status?.toLowerCase()) {
        case 'actif': return '#43e97b';
        case 'terminé': return 'var(--primary-color)';
        case 'en pause': return '#ffc107';
        default: return 'var(--primary-color)';
      }
    }};
    border: 1px solid ${props => {
      switch (props.status?.toLowerCase()) {
        case 'actif': return 'rgba(67, 233, 123, 0.3)';
        case 'terminé': return 'rgba(102, 126, 234, 0.3)';
        case 'en pause': return 'rgba(255, 193, 7, 0.3)';
        default: return 'rgba(102, 126, 234, 0.3)';
      }
    }};
    font-weight: 600;
    font-size: 0.8rem;
  }
`;

const AmountCell = styled.span`
  font-weight: 600;
  color: var(--primary-color);
  background: rgba(102, 126, 234, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
`;

const StyledTabs = styled(Tabs)`
  margin-bottom: 1rem;

  .MuiTabs-indicator {
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    height: 3px;
    border-radius: 3px;
  }
`;

const StyledTab = styled(Tab)`
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  min-height: 48px !important;
  color: var(--text-secondary) !important;
  border-radius: 8px;
  margin: 0 4px;
  transition: all 0.3s ease !important;

  &:hover {
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    color: white !important;

    .MuiTab-iconWrapper {
      color: white;
    }

    span {
      color: white;
    }
  }

  &.Mui-selected {
    color: var(--primary-color) !important;
  }

  .MuiTab-iconWrapper {
    margin-right: 8px;
    transition: color 0.3s ease;
  }
`;

const TabBadge = styled.span`
  background: rgba(102, 126, 234, 0.15);
  color: var(--primary-color);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  margin-left: 8px;
  transition: all 0.3s ease;

  ${StyledTab}:hover & {
    background: rgba(255, 255, 255, 0.25);
    color: white;
  }
`;

export default function AdminProjectsTab(props: { selectedMenu: Function }) {
    const { projects, setSelectedProject, selectProjectWithParticipants } = useContext(AdminProjectContext);
    const navigate = useNavigate();
    const projectData = Array.isArray(projects) ? projects : [];
    const [activeTab, setActiveTab] = useState<number>(0);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openedProject, setOpenedProject] = useState<Project>();
    const [openParticipantModal, setOpenParticipantModal] = useState<boolean>(false);
    const [loadingProjectId, setLoadingProjectId] = useState<string | null>(null);

    // Filtrer les projets selon l'onglet actif
    const activeProjects = projectData.filter(p => p.status !== PROJECT_STATUS.ARCHIVED);
    const archivedProjects = projectData.filter(p => p.status === PROJECT_STATUS.ARCHIVED);
    const filteredProjects = activeTab === 0 ? activeProjects : archivedProjects;

    // Gestion de l'ajout de participant - ouvre le modal directement
    const handleAddParticipant = (e: React.MouseEvent, project: Project) => {
        e.stopPropagation();
        setSelectedProject(project);
        setOpenParticipantModal(true);
    };

    // Ouvrir le projet dans le dashboard
    const handleOpenProject = async (e: React.MouseEvent, project: Project) => {
        e.stopPropagation();
        const projectId = project.getProjectId();
        if (!projectId || loadingProjectId) return; // Évite double clic

        setLoadingProjectId(projectId);
        await selectProjectWithParticipants(project);
        localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, projectId);
        navigate('/admin/dashboard');
        setLoadingProjectId(null);
    };

    const statsData = [
        {
            label: "Total des projets",
            value: projects?.length || 0,
            icon: <FolderSpecial />,
            variant: 'primary' as const,
            gradient: "linear-gradient(45deg, #667eea, #764ba2)"
        },
        {
            label: "Projets démarrés",
            value: projects?.filter(p => p.status === PROJECT_STATUS.STARTED).length || 0,
            icon: <TrendingUp />,
            variant: 'success' as const,
            gradient: "linear-gradient(45deg, #43e97b, #38f9d7)"
        },
        {
            label: "Projets archivés",
            value: projects?.filter(p => p.status === PROJECT_STATUS.ARCHIVED).length || 0,
            icon: <CheckCircle />,
            variant: 'info' as const,
            gradient: "linear-gradient(45deg, #4facfe, #00f2fe)"
        }
    ];

    function handleRowClicked(row: Project) {
        setOpenedProject(row);
        setOpenModal(true);
    }

    function handleNewViewClick(view: number) {
        return () => props.selectedMenu(view);
    }

    return (
        <ProjectsContainer>
            <SectionHeader>
                <HeaderLeft>
                    <SectionIcon>
                        <FolderSpecial />
                    </SectionIcon>
                    <SectionTitle>Gestion des projets</SectionTitle>
                </HeaderLeft>
                <AddProjectButton onClick={handleNewViewClick(ADMIN_VIEWS.NEW_PROJECT_VIEW)}>
                    <CreateNewFolder />
                    Nouveau Projet
                </AddProjectButton>
            </SectionHeader>

            {/* Statistiques */}
            <StatsGrid>
                {statsData.map((stat, index) => (
                    <StatCard key={index}>
                        <StatCardContent>
                            <StatIconContainer>
                                {stat.icon}
                            </StatIconContainer>
                            <StatInfo>
                                <StatValue>{stat.value}</StatValue>
                                <StatLabel>{stat.label}</StatLabel>
                            </StatInfo>
                        </StatCardContent>
                    </StatCard>
                ))}
            </StatsGrid>

            {/* Onglets */}
            <StyledTabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                aria-label="Filtrer les projets"
            >
                <StyledTab
                    icon={<PlayArrow />}
                    iconPosition="start"
                    label={
                        <span>
                            Actifs
                            <TabBadge>{activeProjects.length}</TabBadge>
                        </span>
                    }
                />
                <StyledTab
                    icon={<Archive />}
                    iconPosition="start"
                    label={
                        <span>
                            Archivés
                            <TabBadge>{archivedProjects.length}</TabBadge>
                        </span>
                    }
                />
            </StyledTabs>

            {/* Table des projets */}
            <ProjectsTableCard>
                <TableContainer component={Paper} style={{ background: 'transparent' }}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell>Projet</TableCell>
                                <TableCell>Date de début</TableCell>
                                <TableCell>Date de fin</TableCell>
                                <TableCell>Cotisation</TableCell>
                                <TableCell>Montant/prise</TableCell>
                                <TableCell>Statut</TableCell>
                                <TableCell>Effectif</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProjects?.map((project, index) => (
                                <StyledTableRow
                                    key={project.uuid}
                                    onClick={() => handleRowClicked(project)}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <TableCell>
                                        <ProjectInfo>
                                            <ProjectAvatar>
                                                {project.name.charAt(0).toUpperCase()}
                                            </ProjectAvatar>
                                            <ProjectName>{project.name}</ProjectName>
                                        </ProjectInfo>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(project.startDate).toLocaleDateString('fr-FR')}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(project.endDate).toLocaleDateString('fr-FR')}
                                    </TableCell>
                                    <TableCell>
                                        <AmountCell>{formatAmount(project.contribution || 0, project.currency)}</AmountCell>
                                    </TableCell>
                                    <TableCell>
                                        <AmountCell>{formatAmount(project.withdrawalAmount || 0, project.currency)}</AmountCell>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip
                                            label={project.status || 'Non défini'}
                                            size="small"
                                            status={project.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${project.participants || 0} membres`}
                                            icon={<People />}
                                            size="small"
                                            style={{
                                                background: 'rgba(102, 126, 234, 0.1)',
                                                color: 'var(--primary-color)',
                                                border: '1px solid rgba(102, 126, 234, 0.3)'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                            <Tooltip title="Ajouter un participant" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleAddParticipant(e, project)}
                                                        disabled={project.status === PROJECT_STATUS.ARCHIVED}
                                                        sx={{
                                                            color: '#43e97b',
                                                            backgroundColor: 'rgba(67, 233, 123, 0.1)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(67, 233, 123, 0.2)',
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: 'rgba(0, 0, 0, 0.26)',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                            }
                                                        }}
                                                    >
                                                        <PersonAdd fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Ouvrir dans le dashboard" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleOpenProject(e, project)}
                                                        disabled={loadingProjectId === project.getProjectId()}
                                                        sx={{
                                                            color: 'var(--primary-color)',
                                                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(102, 126, 234, 0.2)',
                                                            },
                                                            '&.Mui-disabled': {
                                                                color: 'rgba(0, 0, 0, 0.26)',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNew fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </ProjectsTableCard>

            {openedProject && <AdminProjectEditingModal
                key={openedProject.uuid || openedProject._id}
                openModal={openModal}
                setOpenModal={setOpenModal}
                openedProject={openedProject}
                setOpenedProject={setOpenedProject}
            />}

            <AdminAddProjectParticipantsModal
                openModal={openParticipantModal}
                setOpenModal={setOpenParticipantModal}
            />
        </ProjectsContainer>
    );
}
